import clsx from 'clsx'

import { Text } from '@wartek-id/text-v1'

import CardPeriod from 'app/KinerjaShared/components/CardPeriod'
import { assetUrl } from 'utils/url'
import { PERFORMANCE_PRACTICE_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/performancePractice'
import { PREDICATE_RECOMMENDATION_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/predicateRecommendation'
import { WORK_BEHAVIOR_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/workBehavior'
import { GeneralNoticeState } from 'components/NoticeState'

import type { EvaluationResultData } from 'types/kinerja-pelaksanaan'

export type EvaluationResultProps = {
  data: EvaluationResultData
  period?: number
  year: number
}

const PERIOD_BG_COLOR = { 1: 'bg-purple-20', 2: 'bg-teal-20' }

/**
 * @deprecated Use app/KinerjaShared/components/EvaluationResult.tsx instead
 */
export function EvaluationResult({
  data,
  period,
  year,
}: EvaluationResultProps) {
  const isAnnual = !Number.isInteger(period)
  let periodBgColor = isAnnual ? 'bg-blue-90' : PERIOD_BG_COLOR[period]

  if (!data) {
    return (
      <GeneralNoticeState
        description="Dokumen tidak ditemukan"
        containerClassNames="flex flex-col items-center"
      />
    )
  }

  return (
    <div className="bg-white rounded-3xl overflow-hidden w-full">
      <header
        className={clsx(
          'relative overflow-hidden flex items-center gap-4 px-9 py-6 bg-opacity-50',
          !isAnnual && periodBgColor,
          isAnnual && 'bg-gradient-to-r from-blue-90 to-blue-80'
        )}
      >
        {isAnnual && (
          <img
            alt=""
            role="presentation"
            src={assetUrl('/images/pengelolaan-kinerja/confetti.png')}
            className="absolute right-0 top-0"
            width={144}
          ></img>
        )}
        <div className="flex h-11 items-center gap-3 self-center">
          <img
            src={assetUrl('/images/kemendikbud2x.png')}
            width={42}
            height={42}
            className="max-h-full"
            alt=""
          />
          <img
            src={assetUrl(
              isAnnual
                ? '/icon/e-pengelolaan-kinerja-logo-secondary.png'
                : '/icon/e-pengelolaan-kinerja-logo.png'
            )}
            width={124}
            height={42}
            alt=""
            className="max-h-full"
          />
        </div>
        <Text
          variant="heading-md"
          as="h2"
          color={isAnnual ? 'inverse' : 'default'}
        >
          Hasil Penilaian Kinerja Guru
        </Text>
      </header>
      <div className="max-w-3xl p-9">
        {data && (
          <>
            <div className="flex">
              <section className="space-y-1 w-6/12">
                <Text variant="heading-sm" as="h3">
                  Nama Instansi
                </Text>
                <Text>{data?.dinas.wilayah}</Text>
              </section>
              <section className="space-y-1">
                <Text variant="heading-sm">Periode Penilaian</Text>
                <Text>
                  {isAnnual ? (
                    `1 Januari sampai dengan 31 Desember ${year}`
                  ) : (
                    <CardPeriod
                      period={period}
                      enableMultiplePeriodSupport
                      withYear
                      withDate
                      year={year}
                      separator=" sampai dengan "
                    />
                  )}
                </Text>
              </section>
            </div>
            <hr className="my-9" />
            <section className="space-y-1">
              <Text variant="heading-sm" as="h3">
                Pegawai Yang Dinilai
              </Text>
              <dl className="grid grid-cols-2 gap-y-1">
                <Text as="dt">Nama</Text>
                <Text as="dd">: {data?.teacher.name}</Text>
                <Text as="dt">NIP</Text>
                <Text as="dd">: {data?.teacher.nip}</Text>
                <Text as="dt">Jabatan</Text>
                <Text as="dd">: {data?.teacher.role}</Text>
                <Text as="dt">Unit Kerja</Text>
                <Text as="dd">: {data?.teacher.schoolName}</Text>
              </dl>
            </section>
            <hr className="my-9" />
            <section className="space-y-2">
              <Text variant="heading-sm" as="h3">
                Pejabat Penilai Kinerja
              </Text>
              <dl className="grid grid-cols-2 gap-y-2">
                <Text as="dd">Nama</Text>
                <Text as="dt">: {data?.supervisor.name}</Text>
                <Text as="dd">NIP</Text>
                <Text as="dt">: {data?.supervisor.nip}</Text>
                <Text as="dd">Jabatan</Text>
                <Text as="dt">: {data?.supervisor.role}</Text>
                <Text as="dd">Unit Kerja</Text>
                <Text as="dt">: {data?.supervisor.schoolName}</Text>
              </dl>
            </section>
            <hr className="my-9" />
            <section className="space-y-4">
              <div className="flex space-x-2 items-center">
                <Text variant="heading-sm" as="h3">
                  Predikat Kinerja
                </Text>
                <Text
                  className={clsx(
                    periodBgColor,
                    'h-6 px-2 rounded-full inline-flex items-center'
                  )}
                  variant="body-sm-bold"
                  color={isAnnual ? 'inverse' : 'default'}
                >
                  {isAnnual ? (
                    'Tahunan'
                  ) : (
                    <CardPeriod
                      enableMultiplePeriodSupport
                      period={period}
                      withYear={false}
                      year={year}
                    />
                  )}
                </Text>
              </div>
              <div className="rounded border border-subdued ">
                {!isAnnual && (
                  <div className="pt-2 pb-6">
                    <div className="flex items-center px-6 py-4 space-x-4 justify-between">
                      <div className="space-y-1">
                        <Text variant="heading-sm">Praktik Kinerja</Text>
                        <Text variant="body-sm" color="disabled">
                          Berdasarkan dokumen Tindak Lanjut dan Refleksi Tindak
                          Lanjut.
                        </Text>
                      </div>
                      <Text
                        variant="heading-sm"
                        color="subdued"
                        className="flex-shrink-0 w-44"
                      >
                        {
                          PERFORMANCE_PRACTICE_RESULT_TEXT[
                            data?.predicate.performancePractice
                          ]
                        }
                      </Text>
                    </div>
                    <div className="flex items-center px-6 py-4 space-x-4 justify-between">
                      <div className="space-y-1">
                        <Text variant="heading-sm">Perilaku Kerja</Text>
                        <Text variant="body-sm" color="disabled">
                          Berdasarkan penilaian terhadap 7 aspek Perilaku Kerja.
                        </Text>
                      </div>
                      <Text
                        variant="heading-sm"
                        color="subdued"
                        className="flex-shrink-0 w-44"
                      >
                        {
                          WORK_BEHAVIOR_RESULT_TEXT[
                            data?.predicate.workBehavior
                          ]
                        }
                      </Text>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between bg-surface-selected-default px-6 py-4 rounded-b">
                  <Text variant="heading-md" className="w-9/12">
                    {`Predikat Kinerja${!isAnnual ? '*' : ''}`}
                  </Text>
                  <Text variant="heading-md" className="flex-shrink-0 w-44">
                    {
                      PREDICATE_RECOMMENDATION_RESULT_TEXT[
                        data?.predicate.result
                      ]
                    }
                  </Text>
                </div>
              </div>
              {!isAnnual && (
                <Text variant="body-sm-bold" className="!text-blue-60">
                  *Predikat Kinerja untuk bulan{' '}
                  <CardPeriod
                    enableMultiplePeriodSupport
                    period={period}
                    separator="-"
                    withYear={false}
                    year={year}
                  />{' '}
                  hanya digunakan sebagai bahan diskusi bersama atasan untuk
                  peningkatan kinerja berikutnya.
                </Text>
              )}
            </section>
          </>
        )}
        <footer className="mt-9 space-y-4">
          {!isAnnual && (
            <section>
              <Text variant="body-sm" as="h3">
                <strong>Perlu diketahui:</strong>
              </Text>
              <ol className="list list-decimal px-4">
                <Text as="li" variant="body-sm">
                  Dokumen ini diberikan pada bulan Juni dan Desember di Ruang
                  GTK.
                </Text>
                <Text as="li" variant="body-sm">
                  Dokumen ini digunakan Kemendikbudristek untuk keperluan
                  analisis capaian dan upaya peningkatan kinerja.
                </Text>
                <Text as="li" variant="body-sm">
                  Dokumen ini menjadi salah satu komponen untuk memproses
                  Predikat Kinerja tahunan yang akan dialirkan ke e-Kinerja BKN
                  (Badan Kepegawaian Negara).
                </Text>
              </ol>
            </section>
          )}
          <section>
            <Text variant="body-sm" as="h3">
              <strong> Catatan:</strong>
            </Text>

            {isAnnual ? (
              <ol className="list list-decimal px-4">
                <Text as="li" variant="body-sm">
                  Dokumen ini tidak perlu ditandatangani.
                </Text>
                <Text as="li" variant="body-sm">
                  Predikat Kinerja pada dokumen ini akan digunakan untuk
                  konversi Angka Kredit di e-Kinerja BKN (Badan Kepegawaian
                  Negara).
                </Text>
              </ol>
            ) : (
              <Text variant="body-sm">
                Dokumen ini tidak perlu ditandatangani.
              </Text>
            )}
          </section>
        </footer>
      </div>
    </div>
  )
}
